import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import type Store from '@ember-data/store';
import type CompanyModel from 'tio-common/models/company';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type {
  TasProgramInstance,
  AggregatedProgramInstance,
  OpenSearchResponse,
  ProgramsSearchResult,
} from 'tio-common/models/search';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { hash, array } from '@ember/helper';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { or } from 'tio-ui/utilities';
import {
  getModelTypeFromDataset,
  getDefaultSortFields,
  getEssentialFields,
} from '../../utils/dataset-utils';
import { RELATIVE_DATE_PRESETS } from 'tio-common/components/forms/relative-date-range-picker';
import { SkipCache } from '@warp-drive/core/types/request';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types.gts';
import type { ChartDataPoint } from 'tio-common/types/chart';
import type { HTTPMethod } from '@warp-drive/core-types/request';
import type { RelativeDatePreset } from 'tio-common/components/forms/relative-date-range-picker';

function searchQueryBuilder(type: string, query: Record<string, unknown> = {}) {
  const urlOptions: QueryUrlOptions = {
    identifier: { type },
    op: 'query',
    resourcePath: type,
  };
  const body = JSON.stringify(query);
  const url = buildBaseURL(urlOptions);

  return {
    url,
    method: 'POST' as HTTPMethod,
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

interface EmployeeAggregationResult {
  employeeId: string;
  employeeName?: string;
  totalApplications: number;
  applicationStateCounts: Record<string, number>;
  sampleDocument?: unknown;
}

interface ChartData {
  field: string;
  dataPoints: ChartDataPoint[];
  total: number;
}

export interface ProgramsDataProviderSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      limit: number;
      page: number;

      // Dataset selection (for multi-dataset support in analytics)
      selectedDataset?: string; // e.g., 'tas-program-instances', 'people', 'pslf-forms'

      // Program filters
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      courseCashProgramsOnly?: boolean;

      // Course filters
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;

      // Approver filters
      approverId?: string;

      // Employee filters
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;

      // Invoice filters
      includeInvoiceDate?: boolean;

      // Column selection
      selectedColumns?: string[];

      // Sort field (camelCase with :asc/:desc suffix, e.g. 'companyNameKeyword:asc')
      sortField?: string;

      // Generic filters for non-TAS datasets (field key → value, with __gte/__lte for ranges)
      genericFilters?: Record<string, unknown>;

      // Callback for updating query parameters
      updateQueryParameters: (params: Record<string, unknown>) => void;
    };
  Blocks: {
    default: [
      {
        // Data and loading states
        data: ProgramsSearchResult;
        isLoading: boolean;
        isDataLoaded: boolean;
        error?: Error;

        // Program templates for filters
        programTemplates: TasProgramTemplateModel[];
        isLoadingProgramTemplates: boolean;
        selectedProgramTemplate?: TasProgramTemplateModel;

        // Actions
        downloadCsv: () => Promise<unknown>;
        refresh: () => void;
        updateQueryParameters: (params: Record<string, unknown>) => void;

        // Chart-specific functionality
        buildChartData: (field: string) => ChartData | null;
        getAvailableChartFields: () => Array<{ key: string; label: string }>;

        // Utility getters
        totalRecords: number;
        executionTime: number;
      },
    ];
  };
}

export default class ProgramsDataProviderComponent extends Component<ProgramsDataProviderSignature> {
  @service declare store: Store;

  get companyId() {
    return this.args.company?.id;
  }

  // Transform employee aggregation logic from OpenSearchResponseBuilder
  private transformEmployeeAggregation(
    responseBuilder: OpenSearchResponseBuilder<TasProgramInstance>
  ): EmployeeAggregationResult[] {
    const employees = responseBuilder.getTermsAggregation('employees');

    return employees.map((employeeBucket) => {
      // Get application state counts - API returns camelized field names
      const applicationStates = employeeBucket.aggregations?.applicationStates as
        | { buckets?: Array<Record<string, unknown>> }
        | undefined;
      const stateCounts: Record<string, number> = {};

      if (applicationStates?.buckets) {
        applicationStates.buckets.forEach((stateBucket: Record<string, unknown>) => {
          stateCounts[String(stateBucket.key)] = stateBucket.docCount as number;
        });
      }

      // Get sample document - API returns camelized field names in _source
      let sampleDoc: TasProgramInstance | undefined;
      const sampleDocument = employeeBucket.aggregations?.sampleDocument as
        | { hits?: { hits?: Array<{ _source?: TasProgramInstance }> } }
        | undefined;

      if (sampleDocument?.hits?.hits?.[0]?._source) {
        sampleDoc = sampleDocument.hits.hits[0]._source;
      }

      return {
        employeeId: employeeBucket.key,
        employeeName: sampleDoc?.employeeNameKeyword,
        totalApplications: employeeBucket.count,
        applicationStateCounts: stateCounts,
        sampleDocument: sampleDoc,
      };
    });
  }

  get results(): ProgramsSearchResult {
    const response = this.searchData.value;
    const isLoading = this.searchData.isLoading;
    if (!response) {
      return { items: [], total: 0, took: 0, isLoading };
    }

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    const dataset = this.args.selectedDataset || 'tas-program-instances';

    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - extract documents from hits and return SearchModelWith format
      const items = responseBuilder.getDocuments();
      return {
        items,
        total: responseBuilder.getTotalCount(),
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    } else {
      // Aggregated mode - use our local transformer for employee aggregations
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformEmployeeAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      // Map to the AggregatedProgramInstance format expected by the component
      const allItems: AggregatedProgramInstance[] = employeeResults.map((result) => {
        const programInstanceId =
          (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceIdKeyword ||
          `unknown-${result.employeeId}`;
        return {
          employeeId: result.employeeId,
          // API returns camelCase field names in response
          employeeNameKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeeNameKeyword ||
            `Employee ${result.employeeId}`,
          employeePayrollIdKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeePayrollIdKeyword || '',
          programInstanceId,
          tasProgramInstanceIdKeyword: programInstanceId, // Canonical field name for column mapping
          tasProgramInstanceCreatedAtDate:
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceCreatedAtDate || '',
          // Program name from template (correct field)
          tasProgramTemplateFieldProgramNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program',
          // DEPRECATED: Alias for backward compatibility with old column selections
          // Users may have selected this field, but the correct field is tasProgramTemplateFieldProgramNameKeyword
          tasProgramInstanceFieldNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword ||
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceFieldNameKeyword ||
            'Unknown Program',
          tasProgramInstanceFieldSchoolInstitutionNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramInstanceFieldSchoolInstitutionNameKeyword || '',
          applicationStateCounts: result.applicationStateCounts,
          totalApplications: result.totalApplications,
        };
      });

      // Apply client-side pagination to the aggregated results
      const { page = 1, limit = 25 } = this.args;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedItems = allItems.slice(startIndex, endIndex);

      return {
        items: paginatedItems,
        total: allItems.length, // Total number of unique employees (aggregation buckets)
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    }
  }

  /**
   * Calculate actual date range based on relative preset
   * Returns the dates to use for the query (recalculated if preset is active)
   */
  getApplicationDateRange(): { start?: string; end?: string } {
    // If a relative preset is selected (not 'custom'), recalculate the dates
    const presetId = this.args.applicationDateRangePreset;

    if (presetId && presetId !== 'custom') {
      const preset = RELATIVE_DATE_PRESETS.find((p: RelativeDatePreset) => p.id === presetId);
      if (preset) {
        const { start, end } = preset.getDates();
        return { start, end };
      }
    }

    // Otherwise, use the stored dates as-is (custom mode or no preset)
    return {
      start: this.args.applicationCreatedAtStart,
      end: this.args.applicationCreatedAtEnd,
    };
  }

  buildSearchQuery() {
    const dataset = this.args.selectedDataset || 'tas-program-instances';

    if (dataset !== 'tas-program-instances') {
      const builder = new OpenSearchQueryBuilder().mustTerm('company_id', this.companyId);

      // Apply generic filters from the DatasetFilters component
      const filters = this.args.genericFilters;
      if (filters) {
        // Collect range filter fields (keys ending in __gte or __lte)
        const rangeFields = new Map<string, { gte?: string; lte?: string }>();

        for (const [key, value] of Object.entries(filters)) {
          if (value === undefined || value === null || value === '') continue;

          if (key.endsWith('__gte') || key.endsWith('__lte')) {
            // Range filter — extract field name and bound
            const fieldKey = key.replace(/__(?:gte|lte)$/, '');
            // Convert camelCase field key to snake_case for OpenSearch
            const snakeField = fieldKey.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
            if (!rangeFields.has(snakeField)) {
              rangeFields.set(snakeField, {});
            }
            const range = rangeFields.get(snakeField)!;
            if (key.endsWith('__gte')) {
              range.gte = String(value);
            } else {
              range.lte = String(value);
            }
          } else if (typeof value === 'boolean') {
            builder.mustTerm(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          } else if (Array.isArray(value) && value.length > 0) {
            builder.mustTerms(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          } else if (typeof value === 'string') {
            builder.mustTerm(
              key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
              value
            );
          }
        }

        // Apply collected range filters
        for (const [field, bounds] of rangeFields) {
          builder.range(field, bounds.gte, bounds.lte);
        }
      }

      return builder;
    }

    // Get the actual date range (recalculated if using a relative preset)
    const applicationDateRange = this.getApplicationDateRange();

    return new OpenSearchQueryBuilder()
      .mustTerm('company_id', this.companyId)
      .mustTerm('tas_program_template_id_keyword', this.args.programTemplateId)
      .mustTerm(
        'tas_program_template_field_voucher_program_boolean',
        this.args.excludeNonVoucherPrograms && true
      )
      .when(this.args.courseCashProgramsOnly, (b) => {
        b.addFilter({
          range: {
            tas_application_course_cash_reserved_amount_integer: { gt: 0 },
          },
        });
      })
      .range(
        'tas_program_instance_created_at_date',
        this.args.programCreatedAtStart,
        this.args.programCreatedAtEnd
      )
      .range(
        'tas_application_created_at_date',
        applicationDateRange.start,
        applicationDateRange.end
      )
      .when(this.args.coursesBeginDate || this.args.coursesEndDate, (b) => {
        // coursesBeginDate and coursesEndDate define a date range for filtering courses
        // that both START and END within the specified date range
        // e.g., "Show courses where begin date >= Oct 13 AND begin date <= Oct 14 AND end date <= Oct 14"
        b.exists('tas_application_field_courses_begin_date_date')
          .range(
            'tas_application_field_courses_begin_date_date',
            this.args.coursesBeginDate,
            this.args.coursesEndDate
          )
          .exists('tas_application_field_courses_end_date_date')
          .range(
            'tas_application_field_courses_end_date_date',
            undefined,
            this.args.coursesEndDate
          );
      })
      .mustTerm('employee_id', this.args.employeeId)
      .mustTerm('employment_termination_verified', this.args.excludeTermVerified)
      .mustTerm('tas_application_possible_reviewers', this.args.approverId)
      .when(this.args.terminatedEmployees, (b) => {
        b.range('employment_end_date', undefined, new Date().toISOString().split('T')[0]);
      })
      .mustTermFlexible(
        'tas_application_state_keyword',
        this.args.applicationStates?.split(',').filter(Boolean)
      )
      .when(this.args.paymentStates, (b) => {
        const paymentStateGroups = this.args.paymentStates?.split(',').map((state) => {
          const field = state.replace(/-/g, '_');
          return [
            { exists: { field } },
            {
              range: {
                [field]: {
                  [this.args.excludeNonVoucherPrograms ? 'gte' : 'gt']: 0,
                },
              },
            },
          ];
        });
        if (!paymentStateGroups || !paymentStateGroups.length) {
          return;
        }
        b.shouldEach(paymentStateGroups).setMinimumShouldMatch(1);
      })
      .when(this.args.paymentYear, (b) => {
        const year = Number(this.args.paymentYear);
        b.range('tas_application_payment_last_date_date', `${year}-01-01`, `${year + 1}-01-01`);
      });
  }

  private buildRawOpenSearchQuery() {
    const baseQuery = this.buildSearchQuery();
    const dataset = this.args.selectedDataset || 'tas-program-instances';

    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - apply pagination to the OpenSearch query
      const { page = 1, limit = 25 } = this.args;

      // Use user-selected sort field if provided, otherwise dataset defaults.
      // Sort field arrives as camelCase (FE convention), convert to snake_case for OpenSearch.
      let sortFields: string[];
      if (this.args.sortField) {
        const snakeSort = this.args.sortField.replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`);
        sortFields = [snakeSort];
      } else {
        sortFields = getDefaultSortFields(dataset);
      }
      baseQuery.setPagination(page, limit).setSort(sortFields);

      // Use builder's selectColumns for generic case conversion
      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        const essentialFields = getEssentialFields(dataset);
        baseQuery.selectColumns(this.args.selectedColumns, essentialFields);
      }

      const query = baseQuery.build();

      return {
        query,
        aggs: {},
      };
    } else {
      // Aggregated mode - use the query builder's aggregation methods
      // Note: In aggregation mode, we need to fetch ALL buckets to get accurate total count
      // We'll handle client-side pagination by slicing the results array
      const aggregatedQuery = baseQuery
        .setPagination(0, 0) // Set size to 0 for aggregation-only queries (we only want aggregations)
        .addTermsAggregation('employees', 'employee_id_keyword', 10000) // Fetch all employee buckets
        .addNestedAggregation('employees', 'application_states', {
          terms: {
            field: 'tas_application_state_keyword',
            size: 50,
          },
        });

      // Determine fields for sample document in aggregation
      // Use snake_case for OpenSearch field names in queries
      let sampleDocumentFields = [
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_instance_created_at_date',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
      ];

      // If specific columns are selected, use those plus essential aggregation fields
      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        // Convert camelCase column keys to snake_case for sample document
        const snakeCaseColumns = this.args.selectedColumns.map((col) =>
          col.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`)
        );

        const essentialAggregationFields = [
          // Core fields needed for aggregation display
          'employee_name_keyword',
          'employee_payroll_id_keyword',
          'tas_program_instance_id_keyword',
          'tas_program_template_field_program_name_keyword',
          'tas_program_instance_field_school_institution_name_keyword',
          // State and date fields for fallback logic
          'tas_application_state_keyword',
          'tas_program_instance_state_keyword',
          'tas_application_created_at_date',
          'tas_program_instance_created_at_date',
        ];

        sampleDocumentFields = [...new Set([...snakeCaseColumns, ...essentialAggregationFields])];
      }

      aggregatedQuery.addNestedAggregation('employees', 'sample_document', {
        top_hits: {
          size: 1,
          _source: {
            includes: sampleDocumentFields,
          },
          sort: [
            { tas_program_instance_created_at_date: { order: 'desc' } },
            { tas_application_created_at_date: { order: 'desc' } },
          ],
        },
      });

      const builtQuery = aggregatedQuery.build();

      return {
        query: builtQuery,
        aggs: builtQuery.aggs || {},
      };
    }
  }

  searchData = trackedFunction(this, async () => {
    // Determine model type based on selected dataset
    const dataset = this.args.selectedDataset || 'tas-program-instances';
    const modelType = getModelTypeFromDataset(dataset);

    const rawQuery = this.buildRawOpenSearchQuery();

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
    };
    // TODO: Query should be able to find name space.
    const { content } = await this.store.request(searchQueryBuilder('api/v2/search', base));
    // For TAS datasets, response documents are TasProgramInstance; for others, generic records
    return content as OpenSearchResponse<TasProgramInstance | Record<string, unknown>>;
  });

  programTemplatesData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    const companyId = this.args.company?.id;
    if (!companyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      company_id: companyId,
    });
  });

  get selectedProgramTemplate(): TasProgramTemplateModel | undefined {
    if (!this.args.programTemplateId || !this.programTemplatesData.isFinished) {
      return undefined;
    }
    return this.programTemplatesData.value?.find(
      (template: TasProgramTemplateModel) => template.id === this.args.programTemplateId
    );
  }

  downloadCsv = async () => {
    // Note: companyId is optional - global analytics (isGlobalContext=true) doesn't require it
    const rawQuery = this.buildRawOpenSearchQuery();
    const dataset = this.args.selectedDataset || 'tas-program-instances';
    const modelType = getModelTypeFromDataset(dataset);

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
      csv_export: true,
      aggregated_mode: !this.args.showIndividualApplications,
    };

    return await this.store.adapterFor('search').downloadCsv(base);
  };

  refresh = () => {
    this.searchData.retry();
    this.programTemplatesData.retry();
  };

  updateQueryParameters = (params: Record<string, unknown>) => {
    // Convert null values to null/undefined to ensure query parameters are removed from URL
    const processedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => {
        // Convert null values to null to remove query parameters
        if (value === null || value === undefined) {
          return [key, null];
        }
        return [key, value];
      })
    );

    this.args.updateQueryParameters(processedParams);
  };

  buildChartData = (field: string): ChartData | null => {
    const response = this.searchData.value;
    if (!response) return null;

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    // For aggregated mode, extract data from the employee aggregations
    if (!this.args.showIndividualApplications) {
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformEmployeeAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      if (field === 'application_state') {
        const stateAggregate: Record<string, number> = {};
        let total = 0;

        employeeResults.forEach((result) => {
          Object.entries(result.applicationStateCounts).forEach(([state, count]) => {
            stateAggregate[state] = (stateAggregate[state] || 0) + count;
            total += count;
          });
        });

        const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
          category: key,
          value,
          count: value,
          metadata: {
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          },
        }));

        return { field, dataPoints, total };
      }

      if (field === 'employee_name') {
        const dataPoints: ChartDataPoint[] = employeeResults.map((result) => ({
          category: result.employeeId,
          value: result.totalApplications,
          count: result.totalApplications,
          metadata: {
            label: result.employeeName || `Employee ${result.employeeId}`,
          },
        }));

        const total = dataPoints.reduce((sum, point) => sum + point.value, 0);
        return { field, dataPoints, total };
      }
    }

    // For individual mode, build aggregations from the documents
    const items = responseBuilder.getDocuments() as TasProgramInstance[];

    if (field === 'application_state') {
      const stateAggregate: Record<string, number> = {};

      items.forEach((item) => {
        const state = item.tasApplicationStateKeyword || 'unknown';
        stateAggregate[state] = (stateAggregate[state] || 0) + 1;
      });

      const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
        category: key,
        value,
        count: value,
        metadata: {
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        },
      }));

      return { field, dataPoints, total: items.length };
    }

    return null;
  };

  getAvailableChartFields = () => {
    return [
      { key: 'application_state', label: 'Application State' },
      { key: 'employee_name', label: 'Employee Name' },
      { key: 'employee_payroll_id_keyword', label: 'Payroll ID' },
      { key: 'program_name', label: 'Program Name' },
    ];
  };

  get totalRecords() {
    return this.results.total || 0;
  }

  get executionTime() {
    return this.results.took || 0;
  }

  get searchError(): Error | undefined {
    const error = this.searchData.error;
    if (!error || (typeof error === 'object' && Object.keys(error).length === 0)) {
      return undefined;
    }
    if (error instanceof Error) {
      return error;
    }
    // Convert generic error object to Error instance
    return new Error(JSON.stringify(error));
  }

  <template>
    {{yield
      (hash
        data=this.results
        isLoading=this.searchData.isLoading
        isDataLoaded=this.searchData.isFinished
        error=this.searchError
        programTemplates=(or this.programTemplatesData.value (array))
        isLoadingProgramTemplates=this.programTemplatesData.isLoading
        selectedProgramTemplate=this.selectedProgramTemplate
        downloadCsv=this.downloadCsv
        refresh=this.refresh
        updateQueryParameters=this.updateQueryParameters
        buildChartData=this.buildChartData
        getAvailableChartFields=this.getAvailableChartFields
        totalRecords=this.totalRecords
        executionTime=this.executionTime
      )
    }}
  </template>
}
